import React from "react";
import { useTable } from "./hooks";
import { TableSkeleton, Td, TdSkeleton, Th } from "./components";
import { Checkbox } from "../Form/component";
import { EmptyData } from "../EmptyData/EmptyData";
import { TableProps } from "./interfaces";

export default function Table({
  columns,
  data,
  getRowTextClassName,
  isLoading,
  minRowHeight,
  onRowClick,
  openCommentModal,
  paginator,
  redirectToConcessions,
  sortFunction,
  renderAfterLoading = false,
  isRowDisabled,
  title,
  withSelection = false,
  selectedItems = [],
  onSelectItem,
  onSelectAll,
  isAllSelected = false,
  getItemId = (row) => row.uuid,
}: TableProps) {
  const {
    columnSums,
    getFooterValue,
    isSaleOrCancelMovement,
    shouldCenterRowContent,
    hasRedirectToSaleColumn,
    hasAttemptedLoad,
  } = useTable({
    columns,
    data,
    isLoading,
  });

  const isItemSelected = (row: Record<string, unknown>): boolean => {
    const itemId: string = getItemId(row);
    return selectedItems.includes(itemId);
  };

  return isLoading && data.length === 0 ? (
    <TableSkeleton
      columns={columns}
      minRowHeight={minRowHeight}
      shouldCenterRowContent={
        shouldCenterRowContent && !hasRedirectToSaleColumn
      }
      title={title}
      withSelection={withSelection}
    />
  ) : data.length > 0 ? (
    <>
      <div className="mx-auto flex w-full flex-col gap-5 overflow-auto">
        <div
          className={`mx-auto w-full overflow-auto rounded-2xl border border-gray-200 text-sm text-black ${
            paginator ? "rounded-b-none" : ""
          }`}
        >
          <table className="w-full border-collapse">
            {title && (
              <thead className="bg-white">
                <tr className="flex min-h-[4rem] w-full border-b border-gray-200">
                  <th className="flex flex-1 items-center justify-start px-4 py-3 text-lg font-bold text-gray-900">
                    {title}
                  </th>
                </tr>
              </thead>
            )}
            <thead className="bg-gray-50">
              <tr className="flex min-h-[3rem] w-full border-b border-gray-200">
                {columns.map((column) => (
                  <Th
                    align={column.align}
                    firstFieldKey={column.firstFieldKey}
                    hasSorting={column.hasSorting}
                    isCut={column.isCut}
                    key={column.firstFieldKey}
                    minWidth={column.minWidth}
                    secondFieldKey={column.secondFieldKey}
                    sortFunction={sortFunction}
                    sortKey={column.sortKey}
                    sortKeyName={column.sortKeyName}
                    title={column.title}
                    weight={column.weight}
                  />
                ))}
                {withSelection && (
                  <Th
                    isCheckbox
                    isChecked={isAllSelected}
                    isDisabled={isLoading}
                    onCheckboxChange={onSelectAll}
                    minWidth={60}
                    weight={0}
                  />
                )}
              </tr>
            </thead>
            <tbody>
              {data.map((row, index) =>
                isLoading ? (
                  <tr
                    key={index}
                    className={`flex w-full border-b border-gray-200 bg-white last:border-b-0 hover:relative ${
                      shouldCenterRowContent ? "items-center" : ""
                    }`}
                    style={{
                      minHeight: minRowHeight ? `${minRowHeight}px` : "48px",
                    }}
                  >
                    {columns.map((column) => (
                      <TdSkeleton
                        key={`${index}-${column.firstFieldKey}`}
                        align={column.align}
                        firstFieldType={column.firstFieldType}
                        isFirstFieldSubtitle={column.isFirstFieldSubtitle}
                        isSecondFieldSubtitle={column.isSecondFieldSubtitle}
                        minWidth={column.minWidth}
                        secondFieldKey={column.secondFieldKey}
                        secondFieldType={column.secondFieldType}
                        weight={column.weight}
                      />
                    ))}
                    {withSelection && (
                      <td
                        className="flex-1 p-4 text-center"
                        style={{ minWidth: "60px", flex: 0 }}
                      >
                        <div className="mx-auto h-5 w-5 animate-pulse rounded bg-gray-200" />
                      </td>
                    )}
                  </tr>
                ) : (
                  <tr
                    key={index}
                    className={`group flex w-full border-b border-gray-200 bg-white last:border-b-0 hover:relative 
                      ${
                        isRowDisabled?.(row)
                          ? " cursor-not-allowed border-gray-300 opacity-50"
                          : ""
                      }
                      ${onRowClick ? "cursor-pointer hover:bg-gray-50" : ""} ${
                        shouldCenterRowContent && !isSaleOrCancelMovement(row)
                          ? "items-center"
                          : ""
                      }`}
                    style={{
                      minHeight: minRowHeight ? `${minRowHeight}px` : "48px",
                    }}
                    onClick={() => onRowClick?.(row)}
                  >
                    {columns.map((column) => (
                      <Td
                        key={`${index}-${column.firstFieldKey}`}
                        align={column.align}
                        firstFieldType={column.firstFieldType}
                        firstFieldValue={row[column.firstFieldKey]}
                        hasParent={Boolean(row[column.parentKey || ""])}
                        isFirstFieldSubtitle={column.isFirstFieldSubtitle}
                        isSecondFieldSubtitle={column.isSecondFieldSubtitle}
                        minWidth={column.minWidth}
                        redirectToConcessions={redirectToConcessions}
                        secondFieldType={column.secondFieldType}
                        secondFieldValue={
                          column.secondFieldKey
                            ? row[column.secondFieldKey]
                            : ""
                        }
                        space="body"
                        venueUUID={row.venueUUID || row.venue?.uuid}
                        weight={column.weight}
                        onFunction={column.onFunction}
                        textColor={getRowTextClassName?.(row)}
                        openCommentModal={openCommentModal}
                        row={row}
                        tooltipKey={column.tooltipKey}
                      />
                    ))}
                    {withSelection && (
                      <td
                        className="flex-1 p-4"
                        style={{ minWidth: "60px", flex: 0 }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="flex items-start justify-center">
                          <Checkbox
                            checked={isItemSelected(row)}
                            onChange={() => onSelectItem?.(getItemId(row))}
                          />
                        </div>
                      </td>
                    )}
                  </tr>
                ),
              )}
            </tbody>
            {Object.keys(columnSums).length > 0 && (
              <tfoot className="bg-gray-50">
                <tr className="flex min-h-[3rem] w-full items-center border-t border-gray-200">
                  {columns.map((column) => (
                    <Td
                      key={`footer-${column.firstFieldKey}`}
                      align={column.align}
                      firstFieldType={
                        column.footerFieldKey === "second"
                          ? column.secondFieldType
                          : column.firstFieldType
                      }
                      firstFieldValue={getFooterValue(column)}
                      minWidth={column.minWidth}
                      space="footer"
                      weight={column.weight}
                    />
                  ))}
                  {withSelection && (
                    <td
                      className="flex-1 p-4"
                      style={{ minWidth: "60px", flex: 0 }}
                    />
                  )}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
      {paginator}
    </>
  ) : hasAttemptedLoad.current || renderAfterLoading ? (
    <EmptyData title="No hay datos disponibles" />
  ) : (
    <TableSkeleton
      columns={columns}
      minRowHeight={minRowHeight}
      shouldCenterRowContent={
        shouldCenterRowContent && !hasRedirectToSaleColumn
      }
      withSelection={withSelection}
    />
  );
}
